import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { BarChart3, Loader2, X, ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Tip } from "@/components/Tip";
import { ThemeBadge } from "@/components/ConversationThemes";

const ENERGY_COLORS = {
  low: { bg: "bg-blue-500/10", text: "text-blue-500", label: "Low Energy" },
  medium: { bg: "bg-amber-500/10", text: "text-amber-500", label: "Moderate" },
  high: { bg: "bg-emerald-500/10", text: "text-emerald-500", label: "High Energy" },
};

export function ConversationInsights({ conversationId, messageCount }) {
  const { token } = useAuth();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchInsights = async () => {
    if (!conversationId || loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/chat/insights/${conversationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInsights(data.insights);
      setOpen(true);
    } catch (err) {
      toast.error("Failed to generate insights");
    } finally {
      setLoading(false);
    }
  };

  if (!conversationId || messageCount < 4) return null;

  if (!open) {
    return (
      <Tip content="Emotional breakdown">
        <button
          data-testid="insights-btn"
          onClick={fetchInsights}
          disabled={loading}
          className="p-2 rounded-full hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" strokeWidth={1.5} />}
        </button>
      </Tip>
    );
  }

  const energy = ENERGY_COLORS[insights?.energy_level] || ENERGY_COLORS.medium;
  const moodArc = insights?.mood_arc || [];

  return (
    <div
      data-testid="insights-panel"
      className="mx-auto max-w-3xl mb-4 bg-card/80 border border-primary/15 rounded-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 shadow-sm"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/20">
        <BarChart3 className="w-4 h-4 text-primary flex-shrink-0" />
        <span className="text-sm font-medium font-emphasis flex-1">Conversation Insights</span>
        <button
          data-testid="insights-close"
          onClick={() => setOpen(false)}
          className="p-1 rounded-full hover:bg-muted/50 text-muted-foreground"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {insights && (
        <div className="p-4 space-y-3">
          {/* Top Row: Emotion + Energy + Emojis */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium capitalize">
              {insights.dominant_emotion || "Reflective"}
            </span>
            <span className={cn("px-2.5 py-1 rounded-full text-xs font-medium", energy.bg, energy.text)}>
              {energy.label}
            </span>
            {insights.emoji_summary && (
              <span className="text-lg ml-auto">{insights.emoji_summary}</span>
            )}
          </div>

          {/* Mood Arc */}
          {moodArc.length >= 2 && (
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-muted-foreground font-medium">Mood arc:</span>
              {moodArc.map((mood, i) => (
                <span key={i} className="flex items-center gap-1">
                  <span className="px-2 py-0.5 rounded-full bg-muted/50 text-foreground capitalize">{mood}</span>
                  {i < moodArc.length - 1 && <ArrowRight className="w-3 h-3 text-muted-foreground" />}
                </span>
              ))}
            </div>
          )}

          {/* Themes */}
          {(insights.themes?.length > 0 || insights.detected_themes?.length > 0) && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-muted-foreground font-medium">Themes:</span>
              {(insights.detected_themes || []).map(t => (
                <ThemeBadge key={t} themeKey={t} size="sm" />
              ))}
              {(insights.themes || []).filter(t => !(insights.detected_themes || []).includes(t)).map(t => (
                <span key={t} className="px-2 py-0.5 rounded-full bg-muted/40 text-xs text-foreground capitalize">{t}</span>
              ))}
            </div>
          )}

          {/* Key Moment */}
          {insights.key_moment && (
            <div className="rounded-xl bg-primary/[0.03] border border-primary/10 px-3 py-2">
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles className="w-3 h-3 text-primary" />
                <span className="text-[10px] text-primary font-semibold uppercase tracking-wide">Key Moment</span>
              </div>
              <p className="text-sm text-foreground leading-snug">{insights.key_moment}</p>
            </div>
          )}

          {/* Growth Signal + Needs Attention */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {insights.growth_signal && (
              <div className="rounded-xl bg-emerald-500/[0.04] border border-emerald-500/10 px-3 py-2">
                <span className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wide">Growth</span>
                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{insights.growth_signal}</p>
              </div>
            )}
            {insights.needs_attention && (
              <div className="rounded-xl bg-amber-500/[0.04] border border-amber-500/10 px-3 py-2">
                <span className="text-[10px] text-amber-600 font-semibold uppercase tracking-wide">Follow Up</span>
                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{insights.needs_attention}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
